import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { AccumulativeShadows, OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Chair } from "./Chair";
import { ChairModel, useChair } from "../../../context/ChairContext";
import { useGetChairVariantModel } from "../../../api/hooks/useChairs";
import { Loader } from "../../Loader";
import { useGetFrameTexture } from "../../../api/hooks/useFrame";
import { OrbitControls as OrbitControlsType } from "three-stdlib";
import { mod } from "../../../utilities/helper";
import { usePDFContext } from "../../../context/PDFContext";

const PlaneShadow = () => {
    return (
        <AccumulativeShadows
            resolution={2048}
            frames={20}
            temporal={false}
            color={"#888888"}
            colorBlend={0.8}
            alphaTest={0.6}
            opacity={0.8}
            scale={2}
        >
            <directionalLight intensity={1.1} position={[5, 10, 5]} castShadow={true} shadow-mapSize={[4096, 4096]} />
            <directionalLight intensity={1} position={[3, 10, -7]} castShadow={true} shadow-mapSize={[4096, 4096]} />
        </AccumulativeShadows>
    );
};

const Scene = ({
    chairModel,
    animate,
    moveFinished,
    model,
    frameTexture,
    secondaryFrameTexture,
    showChair,
}: {
    chairModel?: ChairModel;
    animate: boolean;
    moveFinished: boolean;
    model: string;
    frameTexture: string;
    secondaryFrameTexture: string;
    showChair: ChairModel;
}) => {
    const { gl, invalidate } = useThree();
    const setImageBlob = usePDFContext((state) => state.setImageBlob);
    const ref = useRef<OrbitControlsType>(null!);
    const [rotate, setRotate] = useState<boolean>(animate);

    const fullRotations = useRef(0);
    const countRotation = useRef(false);

    const planeShadow = useMemo(() => <PlaneShadow />, [showChair.id]);

    useEffect(() => {
        setRotate(animate);
        if (animate) {
            fullRotations.current = 0;
            countRotation.current = false;
        }
    }, [animate]);

    useFrame(() => {
        const nullPoint = 1.11;
        if (rotate && ref.current) {
            const currentAngle = mod((ref.current.getAzimuthalAngle() - nullPoint) * (-180 / Math.PI), 360);

            if (currentAngle > 180) {
                countRotation.current = true;
            }
            if (countRotation.current && currentAngle < 180) {
                fullRotations.current++;
                countRotation.current = false;
            }

            const distance = currentAngle + 360 * fullRotations.current;

            if (distance < 540) {
                let speed = 0;
                if (distance < 360) {
                    speed = distance + 50;
                } else {
                    speed = 360 * (360 / distance);
                }
                ref.current.autoRotateSpeed = speed / 4;
            } else {
                setRotate(false);
                ref.current.autoRotateSpeed = 0;
                ref.current.autoRotate = false;
                ref.current.setAzimuthalAngle(nullPoint);
            }
            invalidate();
        }
    });

    useEffect(() => {
        if (moveFinished && !rotate) {
            invalidate();

            const timeout = setTimeout(() => {
                const blob = gl.domElement.toDataURL("image/png");

                setImageBlob(blob);
            }, 500);

            return () => clearTimeout(timeout);
        }
    }, [moveFinished, gl, invalidate, setImageBlob]);

    if (!model) {
        return null;
    }

    return (
        <>
            <PerspectiveCamera makeDefault={true} position={[2, 0.75, 1]} fov={50}></PerspectiveCamera>
            <spotLight
                intensity={20}
                decay={1}
                angle={0.1}
                penumbra={0.5}
                position={[5, 10, 5]}
                castShadow={true}
                shadow-mapSize={[4096, 4096]}
            />
            <spotLight
                intensity={10}
                decay={1}
                angle={0.1}
                penumbra={0.5}
                position={[3, 10, -7]}
                castShadow={true}
                shadow-mapSize={[4096, 4096]}
            />
            <ambientLight color={0xffffff} intensity={0.5 * Math.PI} />
            <Suspense fallback={null}>
                <Chair
                    url={model}
                    cover={showChair.cover?.color?.url ?? undefined}
                    frame={frameTexture}
                    secondary={secondaryFrameTexture ?? undefined}
                    repeat={showChair.cover?.catalog?.repeat ?? 1}
                    shininess={{
                        cover: showChair.cover?.catalog?.shininess ?? 30,
                        frame: showChair.frame?.texture?.shininess ?? 30,
                        secondary: showChair.secondaryFrame?.shininess ?? undefined,
                    }}
                    enableClick={!chairModel}
                />
                {chairModel ? null : planeShadow}
            </Suspense>
            <OrbitControls
                ref={ref}
                minPolarAngle={0}
                maxPolarAngle={Math.PI / 2}
                minDistance={1.4}
                maxDistance={chairModel ? 1.8 : 3}
                enableZoom={!chairModel}
                enablePan={false}
                enableRotate={!chairModel}
                target={[0, 0.5, 0]}
                autoRotate={animate}
                autoRotateSpeed={50}
            />
        </>
    );
};
export const ModelViewer = ({ chairModel, animate }: { chairModel?: ChairModel; animate: boolean }) => {
    const { chair } = useChair();
    const showChair = chairModel ?? chair;
    const [moveFinished, setMoveFinished] = useState<boolean>(true);

    const frameQuery = useGetFrameTexture({ id: showChair.frame?.texture?.id ?? "" });
    const secondaryFrameQuery = useGetFrameTexture({ id: showChair.secondaryFrame?.id ?? "" });
    const modelQuery = useGetChairVariantModel({ id: showChair.id });

    const model = modelQuery.data?.chairEntries?.[0]?.variantModel?.[0]?.url ?? "";
    const frameTexture = frameQuery.data?.asset?.url ?? "";
    const secondaryFrameTexture = secondaryFrameQuery.data?.asset?.url ?? "";

    return (
        <>
            <div className={"left"}></div>
            <div className={"right"}></div>
            {modelQuery.isFetching && <Loader className={"model-loader"} />}
            <Canvas
                className={"canvas"}
                shadows={{ enabled: true, type: THREE.PCFShadowMap }}
                gl={{ toneMapping: THREE.NoToneMapping, preserveDrawingBuffer: true }}
                frameloop={"demand"}
                onMouseLeave={() => {
                    setMoveFinished(true);
                }}
                onMouseEnter={() => {
                    setMoveFinished(false);
                }}
                onTouchEnd={() => {
                    setMoveFinished(true);
                }}
                onTouchStart={() => {
                    setMoveFinished(false);
                }}
            >
                <Scene
                    animate={animate}
                    chairModel={chairModel}
                    moveFinished={moveFinished}
                    model={model}
                    frameTexture={frameTexture}
                    secondaryFrameTexture={secondaryFrameTexture}
                    showChair={showChair}
                />
            </Canvas>
        </>
    );
};
